import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Html5Qrcode } from 'html5-qrcode';
import {
  Recycle,
  Camera,
  CameraOff,
  Loader2,
  Save,
  User,
  Search,
  CheckCircle2,
  History,
  MapPin,
  X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SCANNER_ID = 'gestion-eco-scanner';

export default function GestionEcoparque() {
  const [wasteTypes, setWasteTypes] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [manualCode, setManualCode] = useState('');
  const [form, setForm] = useState({ waste_type: '', quantity_kg: '', notes: '', lat: null, lng: null });
  const [capturingGeo, setCapturingGeo] = useState(false);
  const scannerRef = useRef(null);
  const stoppingRef = useRef(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/ecoparque/waste-types`)
      .then((r) => r.json())
      .then((d) => setWasteTypes(Array.isArray(d) ? d : []))
      .catch(() => {});
    return () => stopScanner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopScanner = async () => {
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    try {
      if (scannerRef.current) {
        try { await scannerRef.current.stop(); } catch (_) {}
        try { scannerRef.current.clear(); } catch (_) {}
        scannerRef.current = null;
      }
    } finally {
      stoppingRef.current = false;
      setScanning(false);
    }
  };

  const startScanner = async () => {
    if (scanning) return;
    try {
      const instance = new Html5Qrcode(SCANNER_ID);
      scannerRef.current = instance;
      setScanning(true);
      await instance.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        async (decodedText) => {
          await stopScanner();
          await resolveUserFromQR(decodedText);
        },
        () => {}
      );
    } catch (err) {
      console.error(err);
      toast.error('No se pudo iniciar la cámara.');
      await stopScanner();
    }
  };

  const resolveUserFromQR = async (raw) => {
    setLoadingUser(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ecoparque/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_data: raw }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'QR no reconocido');
        return;
      }
      await loadUser(data.user_code);
    } catch (err) {
      console.error(err);
      toast.error('Error al validar el QR');
    } finally {
      setLoadingUser(false);
    }
  };

  const loadUser = async (code) => {
    const clean = (code || '').trim().toUpperCase();
    if (!clean) return;
    setLoadingUser(true);
    try {
      const [uRes, sRes, dRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/ecoparque/users/${encodeURIComponent(clean)}`),
        fetch(`${BACKEND_URL}/api/ecoparque/users/${encodeURIComponent(clean)}/stats`),
        fetch(`${BACKEND_URL}/api/ecoparque/users/${encodeURIComponent(clean)}/deliveries?limit=5`),
      ]);
      if (!uRes.ok) {
        const err = await uRes.json();
        toast.error(err.detail || 'Usuario no encontrado');
        return;
      }
      const uData = await uRes.json();
      const sData = sRes.ok ? await sRes.json() : null;
      const dData = dRes.ok ? await dRes.json() : [];
      setUser(uData);
      setStats(sData);
      setRecent(dData);
      toast.success(`Ciudadano identificado: ${uData.nombre}`);
    } catch (err) {
      console.error(err);
      toast.error('Error cargando datos del ciudadano');
    } finally {
      setLoadingUser(false);
    }
  };

  const handleManualLookup = async (e) => {
    e.preventDefault();
    await loadUser(manualCode);
  };

  const handleSubmitDelivery = async (e) => {
    e.preventDefault();
    if (!user) return;
    if (!form.waste_type || !form.quantity_kg) {
      toast.error('Tipo de residuo y peso son obligatorios');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ecoparque/deliveries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_code: user.user_code,
          waste_type: form.waste_type,
          quantity_kg: parseFloat(form.quantity_kg),
          notes: form.notes || null,
          lat: form.lat,
          lng: form.lng,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'No se pudo registrar la entrega');
        return;
      }
      toast.success(`Entrega registrada: ${data.waste_type} · ${data.quantity_kg} kg · +${data.points} pts`);
      setForm({ waste_type: '', quantity_kg: '', notes: '', lat: null, lng: null });
      // Refrescar stats
      await loadUser(user.user_code);
    } catch (err) {
      console.error(err);
      toast.error('Error de red');
    } finally {
      setSubmitting(false);
    }
  };

  const captureGeolocation = () => {
    if (!('geolocation' in navigator)) {
      toast.error('Geolocalización no disponible en este dispositivo');
      return;
    }
    setCapturingGeo(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({
          ...f,
          lat: parseFloat(pos.coords.latitude.toFixed(6)),
          lng: parseFloat(pos.coords.longitude.toFixed(6)),
        }));
        setCapturingGeo(false);
        toast.success('Ubicación capturada');
      },
      (err) => {
        console.error(err);
        setCapturingGeo(false);
        toast.error('No se pudo capturar la ubicación. Permisos o GPS desactivado.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const reset = () => {
    setUser(null);
    setStats(null);
    setRecent([]);
    setForm({ waste_type: '', quantity_kg: '', notes: '', lat: null, lng: null });
    setManualCode('');
  };

  return (
    <div data-testid="gestion-eco-registrar-page">
      <main className="max-w-7xl mx-auto">
        {!user ? (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-3xl border border-[#CFE3F5] bg-white p-6 shadow-sm">
              <h2 className="font-display text-2xl text-[#0A2540] mb-2">Escanear QR del ciudadano</h2>
              <p className="text-sm text-[#6F8AA8] mb-4">Pide al usuario su código QR de Mi Ecoparque.</p>
              <div
                id={SCANNER_ID}
                className="rounded-2xl overflow-hidden border border-[#CFE3F5] bg-[#F5F9FC] min-h-[280px] flex items-center justify-center text-[#6F8AA8] text-sm"
                data-testid="gestion-eco-scanner"
              >
                {!scanning && <span>Cámara detenida</span>}
              </div>
              <div className="mt-4 flex gap-3">
                {!scanning ? (
                  <button
                    type="button"
                    onClick={startScanner}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1F8A70] text-white text-sm font-medium hover:bg-[#177A60]"
                    data-testid="gestion-eco-start-scan-btn"
                  >
                    <Camera size={16} /> Iniciar escáner
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={stopScanner}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#a8350a]"
                    data-testid="gestion-eco-stop-scan-btn"
                  >
                    <CameraOff size={16} /> Detener
                  </button>
                )}
              </div>
            </div>

            <form
              onSubmit={handleManualLookup}
              className="rounded-3xl border border-[#CFE3F5] bg-white p-6 shadow-sm"
              data-testid="gestion-eco-manual-form"
            >
              <h2 className="font-display text-2xl text-[#0A2540] mb-2">Buscar por código</h2>
              <p className="text-sm text-[#6F8AA8] mb-4">Si el QR no funciona, introduce el código manualmente.</p>
              <label className="block">
                <span className="text-xs font-medium text-[#0A2540] uppercase tracking-wider mb-1.5 block">Código</span>
                <input
                  type="text"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] text-[#0A2540] font-mono uppercase tracking-widest"
                  placeholder="ECO-XXXXX"
                  data-testid="gestion-eco-manual-code"
                />
              </label>
              <button
                type="submit"
                disabled={loadingUser}
                className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1E5AA8] text-white text-sm font-medium hover:bg-[#0F4C97] disabled:opacity-60"
                data-testid="gestion-eco-manual-submit"
              >
                {loadingUser ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                Buscar ciudadano
              </button>
            </form>
          </section>
        ) : (
          <section className="space-y-6">
            {/* Ficha del ciudadano */}
            <div className="rounded-3xl border-2 border-[#1F8A70] bg-gradient-to-br from-[#F0FBF6] to-white p-6" data-testid="gestion-eco-user-card">
              <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-[#1F8A70]/15 flex items-center justify-center">
                    <User size={26} className="text-[#1F8A70]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#6F8AA8] uppercase tracking-wider">Ciudadano identificado</p>
                    <p className="font-display text-2xl text-[#0A2540]" data-testid="gestion-eco-user-name">{user.nombre}</p>
                    <p className="text-sm text-[#6F8AA8]">
                      <span className="font-mono font-semibold text-[#1F8A70]" data-testid="gestion-eco-user-code">{user.user_code}</span>
                      {' · '}{user.email}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={reset}
                  className="px-4 py-2 rounded-full border border-[#CFE3F5] text-sm text-[#0A2540] hover:bg-white"
                  data-testid="gestion-eco-change-user-btn"
                >
                  Cambiar ciudadano
                </button>
              </div>
              {stats && (
                <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <StatBox label="Total kg" value={`${stats.total_kg.toFixed(1)} kg`} color="#1F8A70" />
                  <StatBox label="Puntos" value={stats.total_points.toFixed(0)} color="#1E5AA8" />
                  <StatBox label="Entregas" value={stats.total_entries} color="#0A2540" />
                </div>
              )}
            </div>

            {/* Formulario de entrega */}
            <form
              onSubmit={handleSubmitDelivery}
              className="rounded-3xl border border-[#CFE3F5] bg-white p-6 shadow-sm"
              data-testid="gestion-eco-delivery-form"
            >
              <h3 className="font-display text-xl text-[#0A2540] mb-4 flex items-center gap-2">
                <Recycle size={20} className="text-[#1F8A70]" /> Registrar nueva entrega
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs font-medium text-[#0A2540] uppercase tracking-wider mb-1.5 block">Tipo de residuo*</span>
                  <select
                    value={form.waste_type}
                    onChange={(e) => setForm((f) => ({ ...f, waste_type: e.target.value }))}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] text-[#0A2540] bg-white"
                    data-testid="gestion-eco-waste-type"
                  >
                    <option value="">Selecciona un tipo…</option>
                    {wasteTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-[#0A2540] uppercase tracking-wider mb-1.5 block">Peso (kg)*</span>
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    value={form.quantity_kg}
                    onChange={(e) => setForm((f) => ({ ...f, quantity_kg: e.target.value }))}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] text-[#0A2540]"
                    placeholder="Ej: 12.5"
                    data-testid="gestion-eco-quantity"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-xs font-medium text-[#0A2540] uppercase tracking-wider mb-1.5 block">Notas (opcional)</span>
                  <textarea
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-[#CFE3F5] focus:outline-none focus:ring-2 focus:ring-[#1E5AA8] text-[#0A2540]"
                    placeholder="Observaciones del operario…"
                    data-testid="gestion-eco-notes"
                  />
                </label>
                <div className="block sm:col-span-2">
                  <span className="text-xs font-medium text-[#0A2540] uppercase tracking-wider mb-1.5 block">
                    Ubicación de la entrega (opcional)
                  </span>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={captureGeolocation}
                      disabled={capturingGeo}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#CFE3F5] text-[#1E5AA8] text-sm hover:bg-[#F5F9FC] disabled:opacity-60"
                      data-testid="gestion-eco-capture-geo"
                    >
                      {capturingGeo ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <MapPin size={14} />
                      )}
                      {form.lat != null ? 'Reubicar' : 'Capturar ubicación'}
                    </button>
                    {form.lat != null && form.lng != null && (
                      <span
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F0FBF6] border border-[#1F8A70]/30 text-xs text-[#1F8A70] font-mono"
                        data-testid="gestion-eco-geo-display"
                      >
                        📍 {form.lat.toFixed(5)}, {form.lng.toFixed(5)}
                        <button
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, lat: null, lng: null }))}
                          className="hover:bg-white/40 rounded-full p-0.5"
                          aria-label="Quitar ubicación"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    )}
                    <span className="text-xs text-[#6F8AA8]">
                      Permite alimentar el mapa de orígenes del Ecoparque.
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1F8A70] text-white text-sm font-medium hover:bg-[#177A60] disabled:opacity-60"
                data-testid="gestion-eco-submit-delivery"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Registrar entrega
              </button>
            </form>

            {/* Últimas entregas */}
            <div className="rounded-3xl border border-[#CFE3F5] bg-white p-6 shadow-sm">
              <h3 className="font-display text-xl text-[#0A2540] mb-4 flex items-center gap-2">
                <History size={20} /> Últimas entregas
              </h3>
              {recent.length === 0 ? (
                <p className="text-sm text-[#6F8AA8]">Sin entregas previas para este ciudadano.</p>
              ) : (
                <ul className="divide-y divide-[#CFE3F5]">
                  {recent.map((d) => (
                    <li key={d.id} className="py-3 flex items-center justify-between text-sm">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 size={16} className="text-[#1F8A70]" />
                        <span className="text-[#0A2540] font-medium">{d.waste_type}</span>
                        <span className="text-[#6F8AA8]">{d.quantity_kg} kg</span>
                      </div>
                      <span className="text-xs text-[#6F8AA8] tabular-nums">
                        {new Date(d.created_at).toLocaleString('es-ES')} · +{d.points} pts
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div className="rounded-2xl bg-white border border-[#CFE3F5] p-4 text-center">
      <p className="text-xs text-[#6F8AA8] uppercase tracking-wider mb-1">{label}</p>
      <p className="font-display text-2xl tabular-nums" style={{ color }}>{value}</p>
    </div>
  );
}
